import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
} from '@mui/material';
import {
  FC, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import LicenseContext from 'src/contexts/LicenseContext';
import { OrderSearchConditionEntity } from 'src/entities/OrderSearchCondition.entity';

import {
  convertLegacyConditions, LegacyImportIssue, LegacyImportNotice, LegacyImportResult, LegacyMastersState,
} from './legacyImport';
import { collectLegacyPresetSources, LegacyPresetSource } from './legacyPresetSources';
import { filterRowText } from './rowText';
import {
  FilterColumnDef, FilterRow,
} from './types';

export type LegacyPresetImportDialogProps = {
  open: boolean;
  onClose: () => void;
  /** buildFilterColumns の結果。列・選択肢・トラック / ドライバー候補の解決はこれ 1 つで足りる */
  columns: FilterColumnDef[];
  companyId: string;
  isLicenseReady: boolean;
  isMastersReady: boolean;
  /** マスタ（トラック / ドライバー）の取得が失敗したか。default false */
  isMastersError?: boolean;
  /** 既存の保存済みプリセット名。取り込む名前を重複させないために使う */
  existingPresetNames: string[];
  /** 保存。onSuccess は CREATE が成功したときだけ呼ばれる（useServerFilterPresets.savePreset） */
  onSave: (
    name: string,
    rows: FilterRow[],
    callbacks: { onSuccess: () => void; onError: () => void },
  ) => void;
};

const sectionTitleSx = { fontSize: 12, fontWeight: 600, color: 'hsl(var(--foreground))', m: 0 };
const labelSx = { fontSize: 12 };
/**
 * 注意書きの文字色。テーマの warning (#FFD600) を 50% 暗くした値。
 *
 * 純黄色は白地の 11px では読めず、warning.dark (darken 0.2 = #CCAB00) でもコントラスト比は
 * 2.3 程度しかない。#806B00 なら 5.3:1 で WCAG AA (4.5:1) を満たす。
 *
 * Why not テーマの warning を変える: この色は稼働表やバッジなど、黄色として成立している
 * 箇所でも使われており、文字色の都合で全体を暗くすると別画面の見た目が変わる。
 * Why not sx で theme.colors から darken する: theme を参照するとこのダイアログを描く
 * テストすべてに ThemeProvider が必要になる。同じファイルの mutedSx も色を直書きしている。
 */
const warnSx = { fontSize: 11, color: '#806B00' };
const mutedSx = { fontSize: 11, color: 'hsl(var(--muted-foreground))' };
const rowErrorSx = { fontSize: 11, color: 'error.main' };

/**
 * 一覧の 1 行。チェックボックスとは別に、行そのものをプレビュー切り替えのボタンにする。
 *
 * Why not FormControlLabel でラベルごとチェックボックスに紐付ける: ラベルを押すと
 * チェックが変わってしまい、「プレビューを見るだけ」の操作ができなくなる。
 */
const rowButtonSx = (isPreviewing: boolean) => ({
  flex: 1,
  minWidth: 0,
  textAlign: 'left' as const,
  bgcolor: isPreviewing ? 'hsl(var(--secondary))' : 'transparent',
  border: 'none',
  borderRadius: 1,
  px: 0.5,
  py: 0.5,
  cursor: 'pointer',
  '&:hover': { bgcolor: 'hsl(var(--secondary))' },
});

/**
 * 旧画面（/plans）の項目の表示名と、値の呼び名。取り込めない理由の文言に使う。
 *
 * Why not V3Plans/presenters/OrdersPresenter の SearchOrderProperties を import する:
 * あちらはページの presenter 本体で、hooks・factory・MUI を大量に引き込む。表示名 30 個の
 * ために新地図から旧画面の presenter へ依存させたくない。
 * Why not legacyImport.ts の列マッピングから新画面の列ラベルを引く: 利用者がこの条件を
 * 作ったのは旧画面なので、落ちた条件は旧画面で見ていた名前で示すほうが探しやすい
 * （取り込めた条件のプレビューは逆に、これから使う新画面の列ラベルで出す）。
 */
const LEGACY_KEY_DISPLAY: Record<string, { label: string; valueName: string }> = {
  code: { label: '注文番号', valueName: '文字' },
  memo: { label: '備考', valueName: '文字' },
  shipperName: { label: '荷主名', valueName: '文字' },
  shipperContactPerson: { label: '担当者', valueName: '文字' },
  shipperPhoneNumber: { label: '電話番号', valueName: '文字' },
  shipperEmailAddress: { label: 'メールアドレス', valueName: '文字' },
  itemCount: { label: '数量', valueName: '数値' },
  itemTotalWeightKg: { label: '重量', valueName: '数値' },
  itemTotalVolumeM3: { label: '立米', valueName: '数値' },
  itemPackingStyle: { label: '荷姿', valueName: '文字' },
  itemKlass: { label: '輸送区分', valueName: '文字' },
  itemName: { label: '品名', valueName: '文字' },
  itemHandlingOfCargoStyle: { label: '荷扱', valueName: '文字' },
  itemCanBeMixed: { label: '積み合わせ', valueName: '可否' },
  loadingName: { label: '積地名称', valueName: '文字' },
  loadingAddress: { label: '積地住所', valueName: '文字' },
  loadingStartAt: { label: '積地指定開始時間', valueName: '時刻' },
  loadingEndAt: { label: '積地指定終了時間', valueName: '時刻' },
  unloadingName: { label: '降地名称', valueName: '文字' },
  unloadingAddress: { label: '降地住所', valueName: '文字' },
  unloadingStartAt: { label: '降地指定開始時間', valueName: '時刻' },
  unloadingEndAt: { label: '降地指定終了時間', valueName: '時刻' },
  designatedTruckKlasses: { label: '指定車両タイプ', valueName: '選択肢' },
  designatedTruckCarModels: { label: '指定車種', valueName: '選択肢' },
  designatedTruckLoadingPlatformHeights: { label: '荷台高さ', valueName: '選択肢' },
  designatedTruckLoadingPlatformWidths: { label: '荷台幅', valueName: '選択肢' },
  designatedTruckLoadingPlatformLengths: { label: '荷台長さ', valueName: '選択肢' },
  designatedTruckFloorSpecifications: { label: '床仕様', valueName: '選択肢' },
  designatedTruckFeatures: { label: '装置', valueName: '選択肢' },
  allowedTrucks: { label: '指定トラック', valueName: 'トラック名' },
  deniedDrivers: { label: 'NGドライバー', valueName: 'ドライバー名' },
  chargeBasicFeeYen: { label: '基本運賃', valueName: '数値' },
  chargeHighwayFeeYen: { label: '高速代', valueName: '数値' },
  chargeLoadingFeeYen: { label: '積込料', valueName: '数値' },
  chargeAncillaryFeeYen: { label: '付帯業務料', valueName: '数値' },
  chargeWaitingTimeFeeYen: { label: '待機時間料', valueName: '数値' },
  chargeUnloadingFeeYen: { label: '取卸料', valueName: '数値' },
  chargeExpensesFeeYen: { label: '諸経費', valueName: '数値' },
  chargeAncillaryContent: { label: '付帯業務内容', valueName: '文字' },
};

const GROUP_TITLES: Record<LegacyPresetSource['origin'], string> = {
  company: '会社で共有した条件',
  local: 'このブラウザに保存した条件（他の端末では見えません）',
};

/** プレビューの行頭。新画面のフィルター UI と同じ表記にする（取り込むとこう見える） */
const COMBINATOR_LABELS: Record<FilterRow['combinator'], string> = {
  where: 'Where', and: 'AND', or: 'OR',
};

/** 取り込み元 1 件と、その変換結果（読み込み中は null）。 */
type SourceEntry = {
  key: string;
  source: LegacyPresetSource;
  result: LegacyImportResult | null;
};


/** 落とした条件の理由。値は issue に無いので、元の条件（index で引く）から取る。 */
function issueText(issue: LegacyImportIssue, condition: OrderSearchConditionEntity | undefined): string {
  const display = LEGACY_KEY_DISPLAY[issue.key];
  // 任意項目は項目名そのものがキー（旧画面がそう保存する）ので、そのまま出せる
  const label = display?.label ?? issue.key;
  const value = condition?.value ?? '';

  switch (issue.reason) {
    case 'unknownColumn':
      return `「${label}」はこの画面に無い項目です`;
    case 'unknownCustomField':
      return `任意項目「${label}」が現在の設定にありません`;
    case 'unsupportedOperator':
      return `「${label}」に「${issue.operator}」は使えません`;
    case 'unparsableValue':
      return `値「${value}」を${display?.valueName ?? '値'}として読み取れません`;
    case 'unresolvableId':
      return `「${value}」に一致するトラック / ドライバーがありません`;
    case 'mastersUnavailable':
      return 'トラック・ドライバーを取得できないため、この条件は判定できません';
    case 'tooManyRows':
      return `条件が多く、この画面の形式に収まりません（${issue.rowCount ?? 0} 行）`;
    default:
      return '';
  }
}

/** 旧画面と結果が変わる点。展開だけ行数を含むので関数にする。 */
function noticeText(notice: LegacyImportNotice, rowCount: number): string {
  switch (notice) {
    case 'notContains':
      return '未設定の案件は結果に含まれなくなります';
    case 'itemNameWithSpace':
      return '品名をまたいだ検索はできません。品名ごとの部分一致になります';
    case 'itemNameEquals':
      return '旧画面は品名を連結した文字列との一致でした。品名ごとの一致になるため、複数品名の案件で結果が増えることがあります';
    case 'expanded':
      return `条件式が同じ意味になるよう、条件を ${rowCount} 行に展開しました`;
    case 'revivedColumn':
      return '旧画面ではこの条件が正しく効いていませんでした。取り込むと有効になり、結果が変わります';
    case 'caseInsensitive':
      return '大文字小文字を区別しない検索になります';
    case 'timeAcrossDays':
      return '複数日を表示しているとき、日付をまたぐ案件の判定が変わります';
    case 'idSelectByName':
      return '名前の検索を候補の選択に置き換えました。連結された名前をまたぐ検索は再現できません';
    default:
      return '';
  }
}

/**
 * 保存できない理由。`blocked` は真偽値 1 つなので、issues と行数から原因を絞って文言にする。
 * 判定の順序は「全体不能 → マスタ未取得 → 0 行 → 絞り込みが緩む」。
 */
function blockedReason(result: LegacyImportResult): string | null {
  if (!result.blocked) return null;
  if (result.issues.some((i) => i.reason === 'tooManyRows')) {
    return '条件が多く、この画面の形式に収まらないため取り込めません';
  }
  if (result.issues.some((i) => i.reason === 'mastersUnavailable')) {
    return 'トラック・ドライバーを取得できないため取り込めません';
  }
  if (result.rows.length === 0) return '取り込める条件が 1 件も無いため取り込めません';
  return '取り込めない条件があり、絞り込みが元より広くなるため取り込めません';
}

/**
 * 取り込むときの名前。元の条件名をそのまま使うが、同名があると savePreset が既存を消してから
 * 作り直す（上書き）ため、埋まっている名前は避けて連番を付ける。
 *
 * @param used 既存のプリセット名 ＋ 同じ取り込みの中で既に使った名前
 */
function importName(base: string, used: string[]): string {
  if (!used.includes(base)) return base;
  let suffix = 2;
  while (used.includes(`${base} (${suffix})`)) suffix += 1;
  return `${base} (${suffix})`;
}

/**
 * 旧画面の検索条件を取り込むダイアログ。
 *
 * 取り込み元は「会社で共有した条件」と「このブラウザに保存した条件」の 2 区分。
 * どちらも取得済みの値から集めるので追加リクエストは無い。
 */
export const LegacyPresetImportDialog: FC<LegacyPresetImportDialogProps> = ({
  open, onClose, columns, companyId, isLicenseReady, isMastersReady, isMastersError = false,
  existingPresetNames, onSave,
}) => {
  /**
   * 取り込み元の出どころは license の config（新プリセット一覧と同じ）。
   *
   * Why not props で渡す: この一覧はダイアログだけの関心事で、途中の
   * AdvancedFilterPopover には使い道が無い。context を読めば経路が 1 段短くなる。
   */
  const licenseContext = useContext(LicenseContext);
  const companySearchConditions = licenseContext?.config?.company_search_conditions;

  const columnsByKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);

  /**
   * 取り込み元。localStorage を読むので、開くたびに集め直す（別タブの旧画面が
   * 保存した条件を、閉じて開き直せば拾えるようにする）。
   */
  const sources = useMemo(
    () => (open ? collectLegacyPresetSources(companySearchConditions, companyId) : []),
    [open, companySearchConditions, companyId],
  );

  /**
   * マスタの取得状態で扱いを変える。判定は順序が要る: 取得失敗のときは
   * isMastersReady=false と isMastersError=true が同時に立つため、
   * 「isMastersReady が false なら読み込み中」と単純に見ると失敗を読み込み中と誤判定する。
   */
  const isLoading = !isLicenseReady || (!isMastersError && !isMastersReady);
  const masters = useMemo<LegacyMastersState>(
    () => ({ isLicenseReady, isMastersReady, isMastersError }),
    [isLicenseReady, isMastersReady, isMastersError],
  );

  // 読み込み中は変換を走らせない（候補が揃っていない状態で ID 条件を落としてしまう）。
  // 一覧の「N 件は取り込めません」も出さない。
  const entries = useMemo<SourceEntry[]>(
    () => sources.map((source, index) => ({
      key: `${source.origin}:${index}`,
      source,
      result: isLoading ? null : convertLegacyConditions(source.conditions, columns, masters),
    })),
    [sources, isLoading, columns, masters],
  );

  /** 取り込む対象。プレビューとは独立に選ぶ（複数件を一度に取り込む） */
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  /** 今プレビューしている 1 件。選んだ全件を並べると展開後 36 行 × N 件で読めなくなる */
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  /**
   * 「対象が元より狭くなる」ことへの同意。プレビュー中の 1 件ごとに取るので、
   * 真偽値 1 つではなく取り込み元ごとに持つ。
   */
  const [confirmedKeys, setConfirmedKeys] = useState<string[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const previewed = entries.find((e) => e.key === previewKey) ?? null;
  const result = previewed?.result ?? null;
  const checkedEntries = useMemo(
    () => entries.filter((e) => checkedKeys.includes(e.key)),
    [entries, checkedKeys],
  );

  /**
   * 直近に見えていた会社。役目は 2 つ。
   * (a) 会社が変わったことの検知。前回値と比べるので、呼び出し側が onClose に
   *     インライン関数を渡して identity が毎レンダー変わっても誤発火しない
   * (b) onSuccess は保存の応答（非同期）で呼ばれる。クロージャが捕捉するのは
   *     レンダー時の会社なので、「現在の会社」はここから読む
   */
  const companyIdRef = useRef(companyId);
  /**
   * 開いているか。1 件ずつ順に保存するあいだにキャンセルされたら、残りを保存しない。
   * 応答（非同期）から読むので、レンダー時の値を捕まえる props ではなく ref で持つ。
   */
  const isOpenRef = useRef(open);
  useEffect(() => { isOpenRef.current = open; }, [open]);

  useEffect(() => {
    if (companyIdRef.current === companyId) return;
    companyIdRef.current = companyId;
    // 会社が変わると列も候補も別物になる。進行中の取り込みを破棄してダイアログを閉じる
    setCheckedKeys([]);
    setPreviewKey(null);
    setConfirmedKeys([]);
    setSaveError(null);
    setIsSaving(false);
    onClose();
  }, [companyId, onClose]);

  // 開くたびに選択をリセットする。取り込んだ直後に開き直したとき、前回の選択が残っていると
  // 今作ったプリセットをもう一度保存してしまう（savePreset は同名を消してから作り直す）。
  useEffect(() => {
    if (!open) return;
    setCheckedKeys([]);
    setPreviewKey(null);
    setConfirmedKeys([]);
    setSaveError(null);
    setIsSaving(false);
  }, [open]);

  const toggleChecked = (key: string) => {
    setSaveError(null);
    setCheckedKeys((keys) => (keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]));
  };

  const blocked = blockedReason(result ?? { rows: [], issues: [], notices: [], blocked: false, needsConfirmation: false });
  /**
   * 取り込める状態か。`needsConfirmation` はここに含めない（確認はプレビュー中の 1 件でしか
   * 取れないため、押した時点で未確認のものを名前つきで知らせて中止する）。
   */
  const canImport = !isLoading && !isSaving && checkedEntries.length > 0
    && checkedEntries.every((e) => e.result && !e.result.blocked);

  /**
   * 保存できてから閉じる。失敗したらダイアログを開いたままエラーを出す。
   *
   * Why not 保存後にフィルターへ適用する: 取り込みは複数選べるため、どれを適用すべきかが
   * 決まらない。1 件のときだけ適用すると件数で挙動が変わるので、常に保存だけにする。
   */
  const handleImport = () => {
    if (!canImport) return;
    /**
     * 確認はプレビュー中の 1 件でしか取れない。未確認のものが選択に残ったまま押されたら、
     * どの条件のことなのかを名前で知らせて中止する（黙って取り込むと同意なしに
     * 「対象が元より狭くなる」条件が入る）。
     */
    const unconfirmed = checkedEntries.filter(
      (entry) => entry.result?.needsConfirmation && !confirmedKeys.includes(entry.key),
    );
    if (unconfirmed.length > 0) {
      const names = unconfirmed.map((entry) => `「${entry.source.name}」`).join('');
      setSaveError(`${names}の確認が済んでいません。行を押して内容を確認してください`);
      return;
    }

    const targets = checkedEntries.flatMap((entry) => (
      entry.result ? [{ key: entry.key, name: entry.source.name, rows: entry.result.rows }] : []
    ));
    // 取り込みを開始した時点の会社を捕捉する（応答が返るまでに切り替わり得る）
    const startedCompanyId = companyId;
    // 同じ取り込みの中で作った名前も突き合わせる（2 件目が 1 件目を上書きしないように）
    const usedNames = [...existingPresetNames];
    setSaveError(null);
    setIsSaving(true);

    /**
     * 1 件ずつ順に保存する。savePreset は同名を消してから作るので、並行に走らせると
     * 互いのセッション更新と衝突する。
     */
    const saveFrom = (index: number) => {
      const target = targets[index];
      // 全件終わったら閉じる（保存できてから閉じる。失敗時は開いたままにする）
      if (!target) { setIsSaving(false); onClose(); return; }

      const name = importName(target.name, usedNames);
      usedNames.push(name);
      onSave(name, target.rows, {
        onSuccess: () => {
          // 捕捉した会社と現在の会社が一致し、まだ開いているときだけ続ける。会社切替も
          // キャンセルもこの画面をアンマウントしないので、ガードが無いと旧会社の条件を
          // 新会社へ保存したり、閉じたあとも残りを保存し続けたりする
          if (startedCompanyId !== companyIdRef.current || !isOpenRef.current) {
            setIsSaving(false);
            return;
          }
          // 保存できたものは選択から外す。押し直したときに同じ条件を二重に作らないため
          setCheckedKeys((keys) => keys.filter((k) => k !== target.key));
          saveFrom(index + 1);
        },
        onError: () => {
          setIsSaving(false);
          setSaveError(`「${name}」の保存に失敗しました`);
        },
      });
    };

    saveFrom(0);
  };

  const groupOf = (origin: LegacyPresetSource['origin']) => entries.filter((e) => e.source.origin === origin);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // 取り込み元が数百件あると一覧でダイアログが伸びきり、プレビュー・取り込みボタンが
      // 画面外へ出てしまう（本番の 316 件で実際にそうなった）。高さを決めて中でスクロールさせる
      PaperProps={{ sx: { height: 'min(80vh, 680px)' } }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 700 }}>旧画面の検索条件を取り込む</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box component="p" sx={sectionTitleSx}>{`取り込む条件（${checkedEntries.length} 件選択中）`}</Box>
        {/* 一覧はここでスクロールする。プレビューが長い条件を選んでも 4 行ぶんは残して、
            選び直せなくならないようにする（min-height: 0 だと 0 まで潰れる） */}
        <Box data-testid="legacy-import-source-list" sx={{ flex: 1, minHeight: 112, overflowY: 'auto' }}>
        {entries.length === 0 ? (
          <Box sx={{ ...mutedSx, fontStyle: 'italic', mt: 0.5 }}>取り込める条件がありません</Box>
        ) : (
          (['company', 'local'] as const).map((origin) => (
            groupOf(origin).length > 0 && (
              <Box key={origin} sx={{ mt: 0.5 }}>
                <Box sx={mutedSx}>{GROUP_TITLES[origin]}</Box>
                {groupOf(origin).map((entry) => {
                  // 読み込み中は変換していないので、取り込めるかどうかがまだ分からない
                  const rowReason = entry.result ? blockedReason(entry.result) : null;
                  return (
                    <Box key={entry.key} sx={{ display: 'flex', alignItems: 'flex-start', ml: 0.5 }}>
                      <Checkbox
                        size="small"
                        checked={checkedKeys.includes(entry.key)}
                        // 取り込めない条件は選んでから知らせるのでは遅い。選べないようにする
                        disabled={isLoading || rowReason !== null}
                        onChange={() => toggleChecked(entry.key)}
                        slotProps={{ input: { 'aria-label': entry.source.name } }}
                        sx={{ p: 0.5 }}
                      />
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setPreviewKey(entry.key)}
                        sx={rowButtonSx(previewKey === entry.key)}
                      >
                        <Box component="span" sx={labelSx}>{entry.source.name}</Box>
                        <Box component="span" sx={{ ...mutedSx, ml: 1 }}>
                          {`条件 ${entry.source.conditions.length} 件`}
                        </Box>
                        {rowReason !== null && (
                          <Box component="span" data-testid="legacy-import-row-reason" sx={{ ...rowErrorSx, ml: 1 }}>
                            {rowReason}
                          </Box>
                        )}
                        {rowReason === null && entry.result && entry.result.issues.length > 0 && (
                          <Box component="span" sx={{ ...warnSx, ml: 1 }}>
                            {`⚠ ${entry.result.issues.length} 件は取り込めません`}
                          </Box>
                        )}
                        {previewKey === entry.key && (
                          <Box component="span" sx={{ ...mutedSx, ml: 1 }}>プレビュー中</Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )
          ))
        )}
        </Box>

        {previewed && (
          // プレビュー以降は常に見えるように、スクロール枠の外で縮ませない
          <Box sx={{ flexShrink: 0, mt: 1.5, pt: 1, borderTop: '1px solid hsl(var(--border))' }}>
            <Box component="p" sx={sectionTitleSx}>{`プレビュー（${previewed.source.name}）`}</Box>
            {!result ? (
              <Box sx={{ ...mutedSx, mt: 0.5 }}>トラック・ドライバーを読み込み中です…</Box>
            ) : (
              <Box sx={{ mt: 0.5 }}>
                {/* 展開後 36 行のような長いプレビューでも、一覧やボタンを押し出さないよう
                    行と理由はここでスクロールさせる。取り込み可否に関わる表示は枠の外に置く */}
                <Box
                  data-testid="legacy-import-preview-scroll"
                  sx={{ maxHeight: 176, overflowY: 'auto' }}
                >
                  {result.rows.map((row) => (
                    <Box
                      key={row.id}
                      data-testid="legacy-import-preview-row"
                      sx={{ fontSize: 12, color: 'hsl(var(--foreground))' }}
                    >
                      <Box component="span" sx={{ fontWeight: 700 }}>{`${COMBINATOR_LABELS[row.combinator]} `}</Box>
                      {filterRowText(row, columnsByKey)}
                    </Box>
                  ))}
                  {result.issues.map((issue) => (
                    <Box key={`${issue.index}:${issue.reason}`} data-testid="legacy-import-issue" sx={warnSx}>
                      {`⚠ ${issueText(issue, previewed.source.conditions[issue.index])}`}
                    </Box>
                  ))}
                  {result.notices.map((notice) => (
                    <Box key={notice} data-testid="legacy-import-notice" sx={warnSx}>
                      {`⚠ ${noticeText(notice, result.rows.length)}`}
                    </Box>
                  ))}
                </Box>
                {blocked && (
                  <Box data-testid="legacy-import-blocked" sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>
                    {blocked}
                  </Box>
                )}
                {result.needsConfirmation && (
                  <Box sx={{ mt: 0.5 }}>
                    <Box sx={warnSx}>⚠ この条件を除くと対象が元より狭くなります</Box>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={confirmedKeys.includes(previewed.key)}
                          onChange={(e) => setConfirmedKeys((keys) => (
                            e.target.checked
                              ? [...keys, previewed.key]
                              : keys.filter((k) => k !== previewed.key)
                          ))}
                        />
                      )}
                      slotProps={{ typography: { sx: labelSx } }}
                      label="上記を確認しました"
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ flexShrink: 0, mt: 1.5, pt: 1, borderTop: '1px solid hsl(var(--border))' }}>
          {/* 名前は編集させない。複数件を一度に保存するので、1 件ずつ打たせると手数が増える */}
          <Box sx={{ fontSize: 12, color: 'hsl(var(--foreground))' }}>
            {`${checkedEntries.length} 件を元の名前で取り込みます`}
          </Box>
          {/* ブラウザに保存した個人用の条件も、取り込むと会社の全員に見える */}
          <Box sx={{ ...mutedSx, mt: 0.5 }}>取り込むと、共有フィルターとして保存されます</Box>
          {saveError && (
            <Box data-testid="legacy-import-save-error" sx={{ fontSize: 12, color: 'error.main', mt: 0.5 }}>
              {saveError}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ fontSize: 12 }}>キャンセル</Button>
        <Button variant="contained" disabled={!canImport} onClick={handleImport} sx={{ fontSize: 12 }}>
          {`${checkedEntries.length} 件を取り込む`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
